import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Settings } from 'lucide-react';
import { usePWA } from '../hooks/usePWA';

const NotificationSettings = () => {
  const { registerForNotifications } = usePWA();
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    // Check current notification permission
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }

    // Check if already subscribed to push notifications
    checkSubscriptionStatus();
  }, []);

  const checkSubscriptionStatus = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.log('Error checking subscription status:', error);
      }
    }
  };

  const handleNotificationToggle = async () => {
    if (notificationPermission === 'granted' && isSubscribed) {
      // Unsubscribe from notifications
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            await subscription.unsubscribe();
            setIsSubscribed(false);
          }
        } catch (error) {
          console.error('Error unsubscribing:', error);
        }
      }
    } else {
      // Subscribe to notifications
      const success = await registerForNotifications();
      if (success) {
        setNotificationPermission('granted');
        setIsSubscribed(true);
      }
    }
  };

  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return null; // Don't show if notifications aren't supported
  }

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-rose-500/20 rounded-lg p-2">
            <Settings className="text-rose-400" size={20} />
          </div>
          <div>
            <h3 className="text-white font-medium">Match Notifications</h3>
            <p className="text-gray-300 text-sm">Get notified about new matches</p>
          </div>
        </div>
        
        <button
          onClick={handleNotificationToggle}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            isSubscribed 
              ? 'bg-rose-500 text-white hover:bg-rose-600' 
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
          disabled={notificationPermission === 'denied'}
          title={
            notificationPermission === 'denied' 
              ? 'Notifications blocked. Enable in browser settings.' 
              : isSubscribed 
                ? 'Turn off notifications' 
                : 'Turn on notifications'
          }
        >
          {isSubscribed ? (
            <>
              <Bell size={16} />
              <span>On</span>
            </>
          ) : (
            <>
              <BellOff size={16} />
              <span>Off</span>
            </>
          )}
        </button>
      </div>
      
      {notificationPermission === 'denied' && (
        <div className="mt-3 text-amber-300 text-xs">
          Notifications are blocked. Enable them in your browser settings to receive match alerts.
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;